import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';
import { s3UploadService } from '../../services/s3UploadService';

const FilePreviewModal = ({ isOpen, onClose, file, onDownload, bucketConfig }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (isOpen && file && !file?.isFolder) {
      loadPreview();
    } else {
      setPreviewUrl(null);
      setPreviewError(null);
    }

    return () => {
      // Cleanup preview URL when modal closes
      if (previewUrl && previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen, file]);

  const loadPreview = async () => {
    setLoadingPreview(true);
    setPreviewError(null);

    try {
      // Use the signed URL that was already generated when opening the modal
      if (file?.signedUrl) {
        setPreviewUrl(file?.signedUrl);
      } else if (bucketConfig) {
        // Fallback: generate signed URL if not provided
        await s3UploadService?.initializeClient(bucketConfig);
        const signedUrl = await s3UploadService?.getSignedDownloadUrl(file?.path, 3600);
        setPreviewUrl(signedUrl);
      }
    } catch (err) {
      console.error('[FilePreviewModal] Failed to load preview:', err);
      setPreviewError('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  if (!isOpen || !file) return null;

  const handleDownload = async () => {
    if (!onDownload) {
      console.error('[FilePreviewModal] No download handler provided');
      return;
    }

    setIsDownloading(true);
    try {
      await onDownload(file);
    } catch (err) {
      console.error('[FilePreviewModal] Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isImage = file?.type?.startsWith('image/') || file?.type === 'image';
  const isPDF = file?.type === 'application/pdf' || file?.type === 'document';
  const isVideo = file?.type?.startsWith('video/') || file?.type === 'video';

  return createPortal(
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div className="modal-content max-w-4xl animate-scale-in" onClick={(e) => e?.stopPropagation()}>
        <div className="modal-header">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Icon name="File" size={20} className="text-primary flex-shrink-0" />
            <h2 className="modal-title truncate">{file?.name}</h2>
          </div>
          <button onClick={onClose} className="focus-ring rounded-md p-1 hover:bg-muted">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="bg-muted rounded-lg p-8 mb-6 flex items-center justify-center min-h-[300px]">
            {loadingPreview ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-sm text-muted-foreground">Loading preview...</p>
              </div>
            ) : previewError ? (
              <div className="text-center">
                <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-destructive" />
                <p className="text-sm text-destructive">{previewError}</p>
              </div>
            ) : isImage && previewUrl ? (
              <Image
                src={previewUrl}
                alt={file?.name}
                className="max-w-full max-h-[400px] object-contain rounded-md"
                onError={(e) => {
                  console.error('[FilePreviewModal] Image load error');
                  setPreviewError('Failed to load image');
                }}
              />
            ) : isPDF && previewUrl ? (
              <div className="w-full h-[400px]">
                <iframe
                  src={previewUrl}
                  className="w-full h-full rounded-md"
                  title="PDF Preview"
                />
              </div>
            ) : isVideo && previewUrl ? (
              <video
                src={previewUrl}
                controls
                className="max-w-full max-h-[400px] rounded-md"
                onError={(e) => {
                  console.error('[FilePreviewModal] Video load error');
                  setPreviewError('Failed to load video');
                }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="text-center">
                <Icon name="File" size={64} className="mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No preview available</p>
                <p className="text-xs text-muted-foreground mt-2">Download to view this file</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">File Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">File Name</p>
                <p className="text-sm font-medium text-foreground truncate">{file?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">File Size</p>
                <p className="text-sm font-medium text-foreground font-mono">
                  {formatFileSize(file?.size || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">File Type</p>
                <p className="text-sm font-medium text-foreground">{file?.type || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Last Modified</p>
                <p className="text-sm font-medium text-foreground font-mono">
                  {formatDate(file?.lastModified || new Date())}
                </p>
              </div>
              {file?.path && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">Path</p>
                  <p className="text-sm font-medium text-foreground font-mono truncate">
                    {file?.path}
                  </p>
                </div>
              )}
            </div>

            {file?.metadata && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Metadata</h3>
                <div className="bg-muted rounded-md p-3 space-y-2">
                  {Object.entries(file?.metadata)?.map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-xs text-muted-foreground">{key}</span>
                      <span className="text-xs font-medium text-foreground font-mono">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <Button
            variant="outline"
            iconName="X"
            iconPosition="left"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            iconName="Download"
            iconPosition="left"
            onClick={handleDownload}
            loading={isDownloading}
            disabled={!onDownload}
          >
            Download
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FilePreviewModal;